import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Church, Search, Loader2, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * ParishSearchPicker — searchable parish picker with country/zip filtering.
 * Works with 17K+ parishes without loading them all.
 * 
 * Props:
 *   value: selected parish_id
 *   onChange: (parish_id, parish) => void
 *   label: optional label text
 *   placeholder: optional placeholder
 */
export function ParishSearchPicker({ value, onChange, label, placeholder = "Search by name, city, or zip..." }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedParish, setSelectedParish] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Load selected parish name if value provided but no selection
  useEffect(() => {
    if (value && !selectedParish) {
      axios.get(`${API_BASE}/api/parishes/${value}`).then(r => {
        if (r.data) setSelectedParish(r.data);
      }).catch(() => {});
    }
  }, [value]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API_BASE}/api/parishes`, {
          params: { search: query.trim(), limit: 15 },
          headers,
        });
        setResults(res.data?.parishes || []);
        setShowResults(true);
      } catch {}
      setLoading(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const handleSelect = (parish) => {
    setSelectedParish(parish);
    setQuery("");
    setShowResults(false);
    onChange(parish.parish_id, parish);
  };

  const handleClear = () => {
    setSelectedParish(null);
    setQuery("");
    onChange("", null);
  };

  return (
    <div className="space-y-1.5">
      {label && <label className="text-sm font-medium text-[#2D2A26] font-['Nunito'] block">{label}</label>}
      
      {selectedParish ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#F9F7F2] border border-[#E5E0D5] rounded-md">
          <Church className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-['Nunito'] font-medium text-[#2D2A26] truncate">{selectedParish.name}</p>
            {(selectedParish.location || selectedParish.address || selectedParish.diocese) && (
              <p className="text-[10px] text-[#6B665E] font-['Nunito'] truncate">
                {selectedParish.location || selectedParish.address || selectedParish.diocese}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={handleClear} className="h-6 w-6 p-0 text-[#6B665E] hover:text-red-500">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
          <Input
            value={query}
            onChange={e => { setQuery(e.target.value); if (!e.target.value) setShowResults(false); }}
            onFocus={() => { if (results.length > 0) setShowResults(true); }}
            placeholder={placeholder}
            className="pl-9 border-[#E5E0D5] font-['Nunito'] text-sm"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#6B665E]" />}
          
          {showResults && results.length > 0 && (
            <div className="absolute z-50 top-full left-0 w-full mt-1 bg-white border border-[#E5E0D5] rounded-md shadow-lg max-h-60 overflow-y-auto">
              {results.map(p => (
                <button
                  key={p.parish_id}
                  onClick={() => handleSelect(p)}
                  className="w-full text-left px-3 py-2.5 hover:bg-[#F9F7F2] transition-colors border-b border-[#E5E0D5] last:border-0"
                >
                  <p className="text-sm font-['Nunito'] font-medium text-[#2D2A26]">{p.name}</p>
                  <p className="text-[10px] text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                    {p.location || p.address ? (
                      <><MapPin className="w-3 h-3" />{p.location || p.address}</>
                    ) : p.diocese ? (
                      <>{p.diocese}</>
                    ) : null}
                  </p>
                </button>
              ))}
              {results.length >= 15 && (
                <p className="text-[10px] text-[#9C9689] text-center py-2 font-['Nunito']">Type more to refine results...</p>
              )}
            </div>
          )}
          
          {showResults && query.length >= 2 && results.length === 0 && !loading && (
            <div className="absolute z-50 top-full left-0 w-full mt-1 bg-white border border-[#E5E0D5] rounded-md shadow-lg p-3">
              <p className="text-sm text-[#6B665E] font-['Nunito'] text-center">No parishes found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
